/**
 * Media Processing API Client
 *
 * Unified frontend wrapper for media processing workflows:
 * - QC Analysis
 * - Transcode (format conversion)
 * - Stitch (segment assembly)
 * - VOD Delivery
 *
 * Supports workflow chains for automation
 */

import axios from '@/axios'

export default {
  /**
   * Get available workflows for the organization
   */
  async getAvailableWorkflows () {
    const response = await axios.get('/api/media-processing/workflows')
    return response.data
  },

  /**
   * Get saved workflow chains
   */
  async getWorkflowChains () {
    const response = await axios.get('/api/media-processing/chains')
    return response.data
  },

  /**
   * Save a new workflow chain
   * @param {Object} chain - { name, description, steps }
   */
  async saveWorkflowChain (chain) {
    const response = await axios.post('/api/media-processing/chains', chain)
    return response.data
  },

  /**
   * Submit a media processing job
   * @param {Object} params
   * @param {number} params.inputStorageConfigId - Source storage config
   * @param {string} params.inputPath - Path to source file
   * @param {number} [params.outputStorageConfigId] - Output storage config
   * @param {string} [params.outputPath] - Custom output path
   * @param {Array} [params.workflowSteps] - Array of workflow steps
   * @param {number} [params.chainId] - Use a saved workflow chain
   * @param {number} [params.encodingPresetId] - Encoding preset
   * @param {number} [params.mediaId] - Link to media record
   * @param {number} [params.episodeId] - Link to episode record
   */
  async submitJob (params) {
    const response = await axios.post('/api/media-processing/submit', params)
    return response.data
  },

  /**
   * List recent jobs
   * @param {Object} [params] - { status, type, limit, offset }
   */
  async listJobs (params = {}) {
    const response = await axios.get('/api/media-processing/jobs', { params })
    return response.data
  },

  /**
   * Get job status
   * @param {number} jobId - Job ID
   */
  async getJobStatus (jobId) {
    const response = await axios.get(`/api/media-processing/jobs/${jobId}`)
    return response.data
  },

  /**
   * Cancel a job
   * @param {number} jobId - Job ID
   */
  async cancelJob (jobId) {
    const response = await axios.post(`/api/media-processing/jobs/${jobId}/cancel`)
    return response.data
  },

  /**
   * Poll job status until complete
   * @param {number} jobId - Job ID
   * @param {function} onProgress - Progress callback
   * @param {number} intervalMs - Poll interval (default 3000)
   * @param {number} maxAttempts - Max attempts (default 200)
   */
  async pollJobStatus (jobId, onProgress, intervalMs = 3000, maxAttempts = 200) {
    let attempts = 0

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++
          const result = await this.getJobStatus(jobId)

          if (onProgress) {
            onProgress(result.job.status, result.job.progress, result)
          }

          if (['completed', 'failed', 'cancelled'].includes(result.job.status)) {
            resolve(result)
            return
          }

          if (attempts >= maxAttempts) {
            reject(new Error('Job polling timeout'))
            return
          }

          setTimeout(poll, intervalMs)
        } catch (error) {
          reject(error)
        }
      }

      poll()
    })
  }
}
